import { DecisionGraph } from "./graph-utils/DecisionGraph";
import { type CompilerError, isUuid } from "./MiscUtils";

export interface ReleaseDocument {
  name: string;
  type: "ruledoc" | "rulesheet" | "datatable" | "mapping" | "connection" | "derivedtable";
  version: number;
  reference: string;
  locked?: {
    identity_id: string;
    full_name: string;
  };
  contents: any;
  errors?: CompilerError[];
  /** A temporary flag while the file is being deleted */
  deleted?: boolean;
}

// TODO this type is wrong, it should be above
export type ReleaseDocuments = Record<string, ReleaseDocument>;

export const findReleaseDocument = (
  documents: any,
  predicate: (document: ReleaseDocument, name: string) => boolean,
): ReleaseDocument | undefined => {
  for (const [key, document] of Object.entries(documents || {}) as any) {
    if (document?.reference) {
      if (predicate(document, key)) {
        return document;
      }
    }

    if (typeof document === "object") {
      const found = findReleaseDocument(document, predicate);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
};

export const iterateReleaseDocuments = (
  documents: any,
  func: (document: ReleaseDocument, name: string) => void,
): void => {
  for (const [key, document] of Object.entries(documents || {}) as any) {
    if (document?.reference) {
      func(document, key);
    }

    if (typeof document === "object") {
      iterateReleaseDocuments(document, func);
    }
  }
};

export const iterateReleaseDocumentsAsync = async (
  documents: any,
  callback: (document: ReleaseDocument, name: string) => Promise<void>,
): Promise<void> => {
  for (const [key, document] of Object.entries(documents || {}) as any) {
    if (document?.reference) {
      await callback(document, key);
    }

    if (typeof document === "object") {
      await iterateReleaseDocumentsAsync(document, callback);
    }
  }
};

export const flattenReleaseDocuments = (documents: any): ReleaseDocument[] => {
  const releaseDocuments: ReleaseDocument[] = [];
  iterateReleaseDocuments(documents, (document, name) => {
    releaseDocuments.push(document);
  });
  return releaseDocuments;
};

export const trimDocName = (name: string) =>
  name
    .replace(/\.(docx|xlsx)$/i, "")
    .replace(/<[^>]*>/g, "") // remove html tags
    .trim();

export const sanitizeDocumentName = (name: string) => {
  if (typeof name !== "string") {
    return name;
  }

  let result = (name.startsWith("/") ? name : `/${name}`).trim();
  if (result.endsWith(".docx")) {
    result = result.split(".docx")[0];
  }
  if (result.endsWith(".xlsx")) {
    result = result.split(".xlsx")[0];
  }
  return result.replace(/\t/g, " ").trim();
};

export const isDocumentId = (id: string) => {
  if (!id) {
    return false;
  }
  return isUuid(id) || (id.length === 28 && id.startsWith("00"));
};

export interface GraphForFrontendOptions {
  documentsAsGuids?: boolean;
}

/**
 * The release's documents by reference and by name, for processGraphForFrontend. Each key keeps the first document
 * findReleaseDocument would find for it (the same walk, in the same order), so lookups answer as it does, without
 * walking the release once per lookup.
 */
const releaseDocumentIndex = (release: any) => {
  const byReference = new Map<unknown, ReleaseDocument>();
  const byName = new Map<unknown, ReleaseDocument>();
  iterateReleaseDocuments(release, (document) => {
    if (!byReference.has(document.reference)) byReference.set(document.reference, document);
    if (!byName.has(document.name)) byName.set(document.name, document);
  });
  return { byReference, byName };
};

/**
 * The rule graph as the portal reads it: rule nodes' document references (definedIn, usedIn) as document names, or as
 * document ids with `documentsAsGuids`. Changes the graph's nodes in place.
 */
export const processGraphForFrontend = <T>(
  release: any,
  graph: T,
  options?: GraphForFrontendOptions,
): T => {
  const { documentsAsGuids } = options || {};
  // built on the first lookup: graphs whose references are already in the asked form never walk the release
  let index: ReturnType<typeof releaseDocumentIndex> | undefined;
  const findDocumentById = (id: string) => (index ??= releaseDocumentIndex(release)).byReference.get(id);
  const findDocumentByName = (name: string) => (index ??= releaseDocumentIndex(release)).byName.get(sanitizeDocumentName(name));
  // a node's reference in the asked form; undefined when its document isn't in the release
  const convert = documentsAsGuids
    ? (value: string) => (isDocumentId(value) ? value : findDocumentByName(value)?.reference)
    : (value: string) => (isDocumentId(value) ? findDocumentById(value)?.name : value);
  // a node's references in the asked form, or undefined when they already are
  const converted = (definedIn: unknown, usedIn: unknown) => {
    const changes: { definedIn?: unknown; usedIn?: string[] } = {};
    if (definedIn) {
      const result = convert(definedIn as string);
      if (result !== definedIn) changes.definedIn = result;
    }
    if (Array.isArray(usedIn)) {
      const results: string[] = [];
      for (const value of usedIn) {
        const result = convert(value);
        if (result) {
          results.push(result);
        }
      }
      if (results.length !== usedIn.length || results.some((value, i) => value !== usedIn[i])) changes.usedIn = results;
    }
    return "definedIn" in changes || "usedIn" in changes ? changes : undefined;
  };
  if (graph instanceof DecisionGraph) {
    // read without making node objects; only a node whose references change is written (an edit)
    for (const id of graph.ids()) {
      const changes = converted(graph.field(id, "definedIn"), graph.field(id, "usedIn"));
      if (changes) Object.assign(graph.node(id)!, changes);
    }
  } else {
    // legacy JSON (`nodes: [{ v, value }]`)
    for (const { value: node } of (graph as any)?.nodes ?? []) {
      if (!node) continue;
      const changes = converted(node.definedIn, node.usedIn);
      if (changes) Object.assign(node, changes);
    }
  }

  // don't tell the client we are documentsAsGuids when we are not
  if (!documentsAsGuids) {
    release.documentsAsGuids = undefined;
  }
  return graph;
};
